import { NotificationTargets } from "@modules/notifications/domain/entities/notifications.model";
import { TradeTypeDecisionModalComponent } from "@modules/trade/modals/trade-type-decision-modal/trade-type-decision-modal.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { inject, Injectable } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { NotificationsRemote } from "@modules/notifications/domain/infrastructure/notifications.remote";
import { take } from "rxjs/operators";
import { combineLatest, Observable } from "rxjs";
import { Router } from "@angular/router";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { CryptoTransaction, FiatTransaction } from "@models/transaction";
import { TransactionWrapperModalComponent } from "@modules/transaction/modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LimitOrderNavigationFacade } from "@modules/limit-orders/services/navigation.facade";
import { LimitOrderRouteName } from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { LegacyTradeEntryService } from "@modules/trade/helpers/legacy-trade-entry.service";
import { IOrderListFilterType } from "@modules/limit-orders/interfaces/limit-order.view.interface";
import { AntiPhishingCodeModalService } from "@modules/shared/modals/anti-phishing-code-modal/services/anti-phishing-code-modal.service";
import { PricewatchAddModalComponent } from "@modules/shared/modals/pricewatch-add-modal/components/pricewatch-add-modal.component";
import { PriceAlert } from "@models";
import { AddressBookHoldModalComponent } from "@modules/shared/modals/address-book-on-hold-modal/components/address-book-hold-modal/address-book-hold-modal.component";
import { MarginTradingEntryHelper } from "@modules/margin-trading/services/marign-trading-entry.helper";
import { AccountService } from "@modules/account/services/account.service";
import { OrdersEntryService } from "@modules/limit-orders/services/orders-entry.service";
import { IframeV4EntryHelperService } from "@modules/iframeV4/services/iframeV4.entryhelper.service";
import { VerificationHoldModalComponent } from "@modules/shared/modals/address-book-on-hold-modal/components/verification-hold-modal/verification-hold-modal.component";
import { DepositOnHoldWalletTypeModalComponent } from "@modules/shared/modals/address-book-on-hold-modal/components/deposit-on-hold-wallet-type-modal/deposit-on-hold-wallet-type-modal.component";
import { FEAT_GATES, StatsigClientService } from "@core-services/statsig/statsig.service";

export enum NotificationParams {
	walletPid = "wallet_pid",
	transactionPid = "transaction_pid",
	fiatSymbol = "fiat_symbol",
	assetPid = "asset_pid",
	assetId = "asset_id",
	fiatId = "fiat_id",
	addressBookId = "address_book_id",
	transactionId = "transaction_id",
	txPid = "tx_pid",
	caseID = "case_id",
	type = "type",
	limitOrderPillDefault = "default",
	orderPid = "order_pid"
}

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class NotificationsRoutingFacade {
	private readonly apcModalService = inject(AntiPhishingCodeModalService);
	private readonly ordersEntryService = inject(OrdersEntryService);
	private readonly iframeV4EntryHelperService = inject(IframeV4EntryHelperService);
	private readonly statsigService = inject(StatsigClientService);

	public constructor(
		private readonly modalService: ModalService,
		private readonly router: Router,
		private readonly fiatWalletService: FiatWalletService,
		private readonly cryptoWalletService: CryptoWalletService,
		private readonly tradableListService: TradableListService,
		private readonly remote: NotificationsRemote,
		private readonly limitOrdersNavigation: LimitOrderNavigationFacade,
		private readonly checkoutEntryService: CheckoutEntryHelperService,
		private readonly legacyEntryService: LegacyTradeEntryService,
		private readonly marginTradingEntryHelper: MarginTradingEntryHelper,
		private readonly accountService: AccountService
	) {}

	public getTarget(event: string, eventPid: string): void {
		const target = event.split("?")[0];
		const targetParams = event.split("?")[1];
		const params = new URLSearchParams(targetParams);
		const wallet = params.get(NotificationParams.walletPid);
		const transaction = params.get(NotificationParams.transactionPid);
		const fiatSymbol = params.get(NotificationParams.fiatSymbol);
		const assetPid = params.get(NotificationParams.assetPid);
		const caseID = params.get(NotificationParams.caseID);
		const type = params.get(NotificationParams.type);
		const limitOrderPillDefault = params.get(NotificationParams.limitOrderPillDefault);
		const assetId = params.get(NotificationParams.assetId);
		const fiatId = params.get(NotificationParams.fiatId);
		const addressBookId = params.get(NotificationParams.addressBookId);
		const transactionId = params.get(NotificationParams.transactionId);
		const txPid = params.get(NotificationParams.txPid);
		const orderPid = params.get(NotificationParams.orderPid);

		this.setNotificationAsRead$(eventPid).pipe(take(1)).subscribe();
		switch (target) {
			case NotificationTargets.kyc_review:
				this.router.navigate(["/home/review-data"]);
				break;

			case NotificationTargets.kyc_refresh:
				this.router.navigateByUrl("/kyc-refresh");
				break;
			case NotificationTargets.verification_complete:
				this.goToPortfolio();
				break;
			case NotificationTargets.trade:
				this.modalService.create(TradeTypeDecisionModalComponent, {}, { showCloseButton: false }).present();
				break;
			case NotificationTargets.email:
				this.redirectUserToEmail();
				break;
			case NotificationTargets.cash_plus_auto_roll:
				if (fiatSymbol) {
					this.openFiatWalletBySymbol(fiatSymbol);
				} else {
					this.openFiatWalletTransaction(wallet, transaction);
				}
				break;
			case NotificationTargets.payments_withdrawal:
			case NotificationTargets.payments_deposit:
			case NotificationTargets.generic_fiat_wallet_transaction:
				this.openFiatWalletTransaction(wallet, transaction);
				break;
			case NotificationTargets.weekly_best_payout:
			case NotificationTargets.generic_wallet_transaction:
				this.openWalletTransaction(wallet, transaction);
				break;
			case NotificationTargets.appropriateness_test:
				this.limitOrdersNavigation.gotoByNavigation(LimitOrderRouteName.LimitOrderAppTest);
				break;
			case NotificationTargets.asset_details:
				this.openAssetDetails(assetPid);
				break;
			case NotificationTargets.asset_buy:
				this.openBuyAsset(assetPid);
				break;
			case NotificationTargets.asset_sell:
				this.openSellAsset(assetPid);
				break;
			case NotificationTargets.deposit:
				this.limitOrdersNavigation.openDepositModal();
				break;
			case NotificationTargets.proof_of_founds_upload:
				this.openProofOfFounds(caseID, type, eventPid);
				break;
			case NotificationTargets.portfolio:
				this.goToPortfolio();
				break;
			case NotificationTargets.limit_order_page:
				this.goToOrderList(limitOrderPillDefault);
				break;
			case NotificationTargets.anti_phishing_code:
				this.apcModalService.openAntiPhishingSetupModal();
				break;
			case NotificationTargets.watchlist_add_pa:
				this.openPriceAlertModal(assetId, fiatId);
				break;
			case NotificationTargets.watchlist_add:
				this.ordersEntryService.openBuyOrderFlow(assetId);
				break;
			case NotificationTargets.crypto_deposit_validation:
				this.openAddressBookOnHoldModal(transactionId, addressBookId);
				break;
			case NotificationTargets.crypto_deposit_verification:
				this.openVerificationOnHoldModal(txPid, addressBookId);
				break;
			case NotificationTargets.crypto_withdrawal_verification:
				this.openVerificationOnHoldModal(txPid, addressBookId);
				break;
			case NotificationTargets.user_report:
				this.handleUserReportNotification(targetParams);
				break;
			case NotificationTargets.web3_migration_start:
				this.router.navigate(["/nt-migration"]);
				break;
			case NotificationTargets.limit_order_detail:
				this.marginTradingEntryHelper.openMarginTradingOrderDetail(
					orderPid,
					this.accountService.getDefaultFiat().getId()
				);
				break;
			case NotificationTargets.tin:
				this.iframeV4EntryHelperService.openModal("tin");
				break;
			default:
				this.router.navigate([target]);
		}
	}

	public openWalletTransaction(wallet: string, transaction?: string): void {
		this.router.navigateByUrl(`/wallet/${wallet}`).then(() => {
			if (transaction) {
				this.cryptoWalletService.getTransaction(wallet, transaction).subscribe((ctx: CryptoTransaction) => {
					this.modalService
						.create(TransactionWrapperModalComponent, {
							transaction: ctx,
							canOpen: true
						})
						.present();
				});
			}
		});
	}

	public openFiatWalletTransaction(wallet: string, transaction?: string): void {
		this.router.navigateByUrl(`/fiat-wallet/${wallet}`).then(() => {
			if (transaction) {
				this.fiatWalletService.getTransaction(wallet, transaction).subscribe((ftx: FiatTransaction) => {
					this.modalService
						.create(TransactionWrapperModalComponent, {
							transaction: ftx,
							canOpen: true
						})
						.present();
				});
			}
		});
	}

	public openFiatWalletBySymbol(symbol: string): void {
		const fiatWallet = this.fiatWalletService.list.find((wallet) => wallet.getFiatSymbol() === symbol);
		this.openFiatWalletTransaction(fiatWallet.getId());
	}

	private openBuyAsset(assetPid: string): void {
		const assetId = this.tradableListService.getItemByPid(assetPid).getId();
		this.checkoutEntryService.onTradableSelect(assetId);
	}

	private openSellAsset(assetPid: string): void {
		const assetId = this.tradableListService.getItemByPid(assetPid).getId();
		this.legacyEntryService.openSellNotification(assetId);
	}

	private openAssetDetails(assetPid: string): void {
		const assetId = this.tradableListService.getItemByPid(assetPid)?.getId();
		this.router.navigateByUrl(`/asset/${assetId}`);
	}

	private openProofOfFounds(caseId: string, type: string, eventPid: string): void {
		const route = `/proof-of-funds-upload/${caseId}/${type}`;
		this.router.navigate([route]).then(() => {
			this.router.navigate([], {
				queryParams: {
					pid: eventPid
				}
			});
		});
	}

	private goToPortfolio(): void {
		this.router.navigate(["/portfolio"]);
	}

	private goToOrderList(filterType: string): void {
		const filter =
			filterType === "buy"
				? IOrderListFilterType.Buy
				: filterType === "sell"
					? IOrderListFilterType.Sell
					: filterType === "cancelled"
						? IOrderListFilterType.Cancelled
						: "";
		this.limitOrdersNavigation.goToOrderList(filter);
	}

	public setNotificationAsRead$(pid): Observable<boolean> {
		return this.remote.setNotificationAsRead$(pid);
	}

	public redirectUserToEmail(): void {
		window.location.href = "mailto:support@bitpanda.com";
	}

	private openPriceAlertModal(currencyId: string, fiatId: string): void {
		const priceAlert = new PriceAlert();
		priceAlert.setCurrencyId(currencyId);
		priceAlert.setFiatId(fiatId);
		this.modalService
			.create(PricewatchAddModalComponent, { data: priceAlert }, { showCloseButton: true })
			.present();
	}

	private openAddressBookOnHoldModal(transactionId: string, addressBookId: string): void {
		combineLatest({
			isTravelRuleV2Enabled: this.statsigService.isFeatureActive(FEAT_GATES.TRAVEL_RULE_V2),
			SHW3rdPartyDisabled: this.statsigService.isFeatureActive(FEAT_GATES.SHW_3RD_PARTY_DISABLED)
		}).subscribe(({ isTravelRuleV2Enabled, SHW3rdPartyDisabled }) => {
			if (isTravelRuleV2Enabled) {
				this.modalService.showModal(DepositOnHoldWalletTypeModalComponent, {
					transactionId,
					addressBookId,
					isTravelRuleV2Enabled,
					SHW3rdPartyDisabled
				});
			} else {
				this.modalService.showModal(AddressBookHoldModalComponent, { transactionId, addressBookId });
			}
		});
	}

	public openVerificationOnHoldModal(transactionId: string, addressBookId: string): void {
		const modal = this.modalService.showModal(
			VerificationHoldModalComponent,
			{
				transactionId,
				addressBookId
			},
			{
				preventBackdropDismiss: true,
				preventEscDismiss: true
			}
		);

		modal.present();
	}

	private handleUserReportNotification(targetParams: string): void {
		let [key, value] = targetParams.split("=");
		this.router.navigate(["my-account/reports/list"], { queryParams: { [key]: value } });
	}
}
